import { useEffect, useRef } from "react";
import { useIntersectionObserver } from "../hooks/useIntersectionObserver";
import { useNavbar } from "../context/NavbarContext";
import { useDesignCards } from "../context/DesignCardsContext";
import DesignItem from "./DesignItem";
import DesignNavbar from "./DesignNavbar";

export default function Design() {
  const { setHideNavbar } = useNavbar();
  const { setIsEnded } = useDesignCards();
  const containerRef = useRef(null);
  const containerObserver = useIntersectionObserver(containerRef, {
    rootMargin: "-70% 0px 0px 0px",
  });

  // Hide main navbar when user see this section
  useEffect(() => {
    if (containerObserver?.isIntersecting && window.innerWidth > 820) {
      setHideNavbar(true);
    } else {
      setHideNavbar(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [containerObserver]);

  useEffect(() => {
    const handleScroll = () => {
      const { bottom } = containerRef.current.getBoundingClientRect();
      if (bottom - window.innerHeight <= 0) {
        setIsEnded(true);
      } else {
        setIsEnded(false);
      }
    };
    window.addEventListener("scroll", handleScroll);

    return () => window.removeEventListener("scroll", handleScroll);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <section className="xpb-design" ref={containerRef} id="xpb-design">
      <h3 className="xpb-design__title">
        PASO A PASO PARA <br />
        GENERAR UNA ESTRATEGIA
      </h3>
      <DesignNavbar />
      <DesignItem
        title={
          <h4>
            Investigar{" "}
            <span>
              la situación <br />
              migratoria local
            </span>
          </h4>
        }
        content={
          <>
            <p>
              <strong>¡Sin diagnóstico no hay estrategia!</strong>
            </p>
            <p>
              El primer paso para diseñar una estrategia de comunicación es
              investigar y entender la situación migratoria local.
            </p>
            <p>
              Navega los datos del Laboratorio de migración y percepción
              ciudadana del BID.
            </p>
            <p>
              Para realizar esta investigación, recomendamos incorporar varias
              herramientas a la vez, conocelas aquí, no es necesario hacer
              todas, ¡pero cuantas más, mejor!
            </p>
          </>
        }
        cta={
          <a href="/xenophobia/BID_Mas_herramientas.pdf" download>
            <figure aria-label="Descargar" />
            DESCARGA LA GUÍA DE <br />
            HERRAMIENTAS DE INVESTIGACIÓN
          </a>
        }
        step={1}
      />
      <DesignItem
        title={
          <h4>
            Crear <span>narrativas</span>
          </h4>
        }
        content={
          <>
            <p>
              La narrativa define el "qué" y el "por qué", y permite estructurar
              mensajes que resuenen con el público objetivo. Para eso, es
              esencial contar historias auténticas de personas migrantes y
              refugiadas que generen empatía y comprensión, más allá de la
              compasión. Las narrativas deben reflejar los valores locales -
              identificados a través de la investigación previa -y presentar la
              movilidad humana como una realidad cotidiana y una oportunidad,
              desmitificando prejuicios al mostrar la diversidad del fenómeno
              migratorio. Esto refuerza la historia central de generar un nuevo
              nosotros y abre la mirada hacia la integración.
            </p>
            <p>
              Algunas narrativas que podrían ser interesantes para comunicar
              integración son estas.
            </p>
          </>
        }
        cta={
          <a href="/xenophobia/BID_Disparadores_creativos.pdf" download>
            <figure aria-label="Descargar" />
            DESCARGA LA <br />
            GUÍA DE NARRATIVAS
          </a>
        }
        step={2}
      />
      <DesignItem
        title={
          <h4>
            CONSTRUIR <span>ESTRATEGIA</span>
          </h4>
        }
        content={
          <>
            <p>
              La estrategia define el "cómo" y el "cuándo" en la comunicación.
              Sin estrategia, no hay consistencia ni cohesión en el mensaje. La
              estrategia es el enfoque que adoptamos para alcanzar los objetivos
              de comunicación a largo plazo. Para ello hay que definir un
              objetivo, identificar y segmentar audiencias y canales de
              comunicación, así como encontrar posibles personas voceras que
              puedan replicar nuestro mensaje.
            </p>
            <p>
              Explora estos pasos en profundidad aquí. A la hora de diseñar los
              contenidos, no olvides que cada red y canal tiene sus propios
              formatos y lenguaje, conócelos aquí.
            </p>
          </>
        }
        cta={
          <>
            <a href="/xenophobia/BID_Construir_estrategia.pdf" download>
              <figure aria-label="Descargar" />
              DESCARGA LA GUÍA <br />
              DE ESTRATEGIA
            </a>
            <a href="/xenophobia/BID_Codigos_lenguaje.pdf" download>
              <figure aria-label="Descargar" />
              DESCARGA LA GUÍA <br />
              DE CANALES
            </a>
          </>
        }
        step={3}
      />
      <DesignItem
        title={
          <h4>
            ARMAR <span>EL PLAN DE CONTENIDOS</span>
          </h4>
        }
        content={
          <>
            <p>
              El plan de contenidos - que debe ser como mínimo anual- detalla el
              “qué” queremos comunicar específicamente. Para eso hay que:
            </p>
            <ul>
              <li>· Crear una identidad visual para el contenido</li>
              <li>
                · Definir formatos y frecuencia de publicación por plataforma
              </li>
              <li>
                · Estructurar en un calendario los hitos claves ya identificados
              </li>
            </ul>
            <p>
              Para identificar oportunidades y calendarizarlas, explora los
              documentos que siguen:
            </p>
          </>
        }
        cta={
          <>
            <a href="https://docs.google.com/presentation/d/1hqRtHrTYYvlpXO8QB_dDfki1WjeL99mYMgEIiQmHfjA/edit#slide=id.p">
              <figure aria-label="Descargar" />
              DESCARGA EL MODELO <br />
              DE CALENDARIO
            </a>
            <a href="/xenophobia/BID_Preguntas_guia.pdf" download>
              <figure aria-label="Descargar" />
              DESCARGA LA GUÍA <br />
              DE OPORTUNIDADES
            </a>
          </>
        }
        step={4}
      />
      <DesignItem
        title={
          <h4>
            EVALUAR <span>Y MEJORAR</span>
          </h4>
        }
        content={
          <>
            <p>
              <strong>¡Sin revisión, no hay sostenibilidad!</strong>
            </p>
            <p>
              Hay que mirar las métricas y los comentarios periódicamente para
              identificar nuevas conversaciones y áreas de mejora. Es importante
              tener en cuenta que por razones coyunturales o estructurales es
              probable que la estrategia requiera ser revisada.
            </p>
            <p>
              Clickea aquí para acceder a una breve guía de medición de impacto.
              Aquí encontrarás un informe modelo de cómo mostrar los resultados
              y el impacto
            </p>
          </>
        }
        cta={
          <a href="/xenophobia/BID_Guia_medicion.pdf" download>
            <figure aria-label="Descargar" />
            DESCARGA LA GUÍA DE <br />
            MEDICIÓN DE IMPACTO
          </a>
        }
        step={5}
      />
    </section>
  );
}
